import { Project } from '../types';

export const projects: Project[] = [
  {
    id: '1',
    title: 'Bonhomme Portfolio',
    context: 'Agency: Bonhomme',
    year: 2024,
    techStack: ['Next.js', 'Sanity', 'Lenis', 'Zustand'],
    links: [{ label: 'Soon', url: '' }],
    description:
      'I developed the new portfolio for Bonhomme, with content managed through Sanity for dynamic and flexible updates. The portfolio features carefully crafted animations and transitions, ensuring a seamless and enjoyable user experience.',
    thumbnail: '/thumbnails/bonhomme-portfolio.webp',
    projectMedia: ['/projects/bonhomme/0.webm', '/projects/bonhomme/1.webp'],
  },
  {
    id: '2',
    title: "Soutine's Portraits",
    context: "School Project x Musée de l'Orangerie",
    year: 2023,
    techStack: ['Vue.js', 'GSAP', 'Mediapipe'],
    links: [{ label: 'Website', url: 'https://soutine.netlify.app/' }],
    description:
      "<strong>Soutine's Portraits</strong> is an immersive wall projection experience aimed at making art more accessible in an engaging way. The goal is to immerse users in the world of Soutine's artworks by actively involving them in the restoration of his damaged portraits. To achieve this, users must move in front of the paintings and answer questions related to the artworks. Each correct answer triggers an anecdote about the artist, the history of the portrait, or Paul Guillaume, the museum's gallery owner. Once the restoration is complete, the artwork comes to life through animations.<br /><br />This project was developed in collaboration with the Musée de l'Orangerie. We had two weeks for production: one week for ideation and one week for development, working in a team of 3 developers and 2 designers.",
    thumbnail: '/thumbnails/soutine.webp',
    projectMedia: [
      '/projects/soutine/0.webm',
      '/projects/soutine/1.webm',
      '/projects/soutine/2.webm',
    ],
  },
  {
    id: '3',
    title: "Leïly's Journal",
    context: 'School Project',
    year: 2024,
    techStack: ['React-Three-Fiber', 'Rapier', 'GSAP', 'tiny-emitter'],
    links: [
      { label: 'Website', url: 'https://le-journal-de-leily.netlify.app/' },
      { label: 'Demo Version', url: 'https://demo--le-journal-de-leily.netlify.app/' },
      { label: 'Video Demo', url: 'https://youtu.be/Pn8iTreHANw' },
      { label: 'Video Full', url: 'https://youtu.be/609yH-fqjbc' },
    ],
    description:
      "Through a web-based narrative experience, we tell the story of Leïly, a young Franco-Japanese girl, and her mother. By exploring the 3D scenes of the travel journal they shared, we follow Leïly's journey to Japan, alongside the journey of her mother, who left Japan to settle in France. This immersive work delves into themes of identity, cultural roots, and family bonds.<br /><br />This project is inspired by the true story of Leïly, whom we met during interviews with people of multicultural backgrounds. Her journey to explore her Franco-Japanese roots served as the foundation for our creation.<br /><br />During this 10-week project, we worked as a team of two developers and two designers. As a developer, the main challenges were related to <strong>performance optimization</strong> and managing the <strong>physics</strong> of the characters. I implemented <strong>realistic movement</strong> for character navigation within the 3D scenes while ensuring maximum fluidity. Additionally, the <strong>dynamic folding of 3D objects</strong> in a popup style added another layer of technical complexity, requiring precise synchronization between animation and interaction.",
    thumbnail: '/thumbnails/leily.webp',
    projectMedia: [
      '/projects/leily/0.webm',
      '/projects/leily/1.webm',
      '/projects/leily/2.webm',
      '/projects/leily/3.webm',
    ],
  },
  {
    id: '4',
    title: 'Hestia',
    context: 'School Project',
    year: 2024,
    techStack: ['React-Three-Fiber', 'Redux', 'Lottie', 'Drei'],
    links: [
      { label: 'Website', url: 'https://hestia-experience.netlify.app/' },
    ],
    description:
      "<strong>Hestia</strong> is an interactive experience created in two weeks as part of a workshop on WebGL and binaural sound at Gobelins. Inspired by the theme of the postcard, the project immerses the user in the past by reconstructing the story of a received letter through an immersive scene.<br /><br />The first week focused on ideation (concept, art direction, storytelling, sound design), while the second week was dedicated to production and development. As a developer, I primarily worked on the <strong>dynamic camera</strong>, creating a <strong>path</strong> synchronized with the user's <strong>scroll</strong>, a <strong>camera target</strong> to orient the view around key objects, and a <strong>mouse-reactive movement</strong> to enhance immersion. It was my first time using <strong>React Three Fiber</strong>, which was an enriching experience for handling 3D on the web.<br /><br />The user uncovers the story by collecting objects that rekindle Hestia's hearth, illuminating and animating the scene to piece together the past.",
    thumbnail: '/thumbnails/hestia.webp',
    projectMedia: [
      '/projects/hestia/0.webm',
      '/projects/hestia/1.webm',
      '/projects/hestia/2.webm',
    ],
  },
  {
    id: '5',
    title: 'Tag Heuer - Xmas',
    context: 'Agency: Bonhomme, Client: Tag Heuer',
    year: 2024,
    techStack: ['VanillaJS', 'Lottie', 'GSAP'],
    links: [
      {
        label: 'Website',
        url: 'https://www.tagheuer.com/fr/fr/holiday-season-game.html',
      },
    ],
    description:
      'This project for Tag Heuer involved developing a mobile web game for Christmas, where the goal was to assemble a watch in the correct order as quickly as possible. I was responsible for the development of both the pre-game and post-game sections, particularly focusing on animations and transitions to ensure a smooth and engaging user experience.',
    thumbnail: '/thumbnails/tagheuer-xmas.webp',
    projectMedia: ['/projects/th-xmas/0.webm', '/projects/th-xmas/1.webm'],
  },
  {
    id: '6',
    title: 'Tag Heuer - Golf',
    context: 'Agency: Bonhomme, Client: Tag Heuer',
    year: 2024,
    techStack: ['VanillaJS', 'anime.js'],
    links: [
      {
        label: 'Site Web',
        url: 'https://www.tagheuer.com/fr/fr/smartwatches/golf.html',
      },
    ],
    description:
      'I developed several pages for the Tag Heuer website, including this one dedicated to the Calibre E4 Golf Edition watch. The page features numerous scroll-based animations to create an immersive and dynamic user experience.',
    thumbnail: '/thumbnails/tagheuer-golf.webp',
    projectMedia: ['/projects/th-golf/0.webm', '/projects/th-golf/1.webm'],
  },
  {
    id: '7',
    title: 'Tag Heuer - Takeovers',
    context: 'Agency: Bonhomme, Client: Tag Heuer',
    year: 2024,
    techStack: ['VanillaJS', 'GSAP'],
    description:
      "I developed multiple animations and pop-ups (takeovers) for the Tag Heuer website to showcase the brand's latest releases. My role involved creating engaging animations and optimizing transitions to provide a smooth and dynamic user experience.",
    thumbnail: '/thumbnails/tagheuer-takeovers.webp',
    projectMedia: [
      '/projects/th-takeovers/0.webm',
      '/projects/th-takeovers/1.webm',
      '/projects/th-takeovers/2.webm',
      '/projects/th-takeovers/3.webp',
    ],
  },
];
